import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import baseURL from '../../Components/url';
import moneda from '../../Components/moneda';
import logo from '../../images/logo.png';
import { getConfig, fetchConfigFromDB } from '../../utils/configService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUtensils,
    faSearch,
    faTimes,
    faPlus,
    faBell,
    faFire,
    faTrash,
    faReceipt,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './MenuDigital.css';

export default function MenuDigital() {
    const [searchParams] = useSearchParams();
    const routeParams = useParams();

    // Estado de Mesa
    const [mesaActiva, setMesaActiva] = useState(null);
    const [verificandoMesa, setVerificandoMesa] = useState(true);

    // Catálogo
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('todas');
    const [busqueda, setBusqueda] = useState('');

    // Modalidad de Precios
    const [usarPrecios, setUsarPrecios] = useState(getConfig().usarPrecios !== 'no');

    // Carrito de Mesa
    const [cart, setCart] = useState([]);
    const [drawerCarritoAbierto, setDrawerCarritoAbierto] = useState(false);
    const [nombreCliente, setNombreCliente] = useState('');
    const [notaCocina, setNotaCocina] = useState('');
    const [enviandoPedido, setEnviandoPedido] = useState(false);

    // Modal Llamar Mesero
    const [modalMeseroAbierto, setModalMeseroAbierto] = useState(false);
    const [motivoMesero, setMotivoMesero] = useState('Atención en mesa');
    const [llamandoMesero, setLlamandoMesero] = useState(false);

    // Modal Pedidos Activos de la Mesa (KDS tracker)
    const [modalPedidosActivos, setModalPedidosActivos] = useState(false);
    const [pedidosMesa, setPedidosMesa] = useState([]);

    const verificarTokenMesa = useCallback(async (token) => {
        setVerificandoMesa(true);
        try {
            const res = await fetch(`${baseURL}/mesaTokenGet.php?token=${encodeURIComponent(token)}`);
            const data = await res.json();
            if (data.mesa) {
                setMesaActiva(data.mesa);
                setNombreCliente(`Cliente ${data.mesa.mesa || 'Mesa'}`);
                localStorage.setItem('mesaActiva', JSON.stringify(data.mesa));
                cargarPedidosMesa(data.mesa.idMesa);
            } else {
                toast.error('El código QR de la mesa no es válido');
            }
        } catch (e) {
            console.error('Error al verificar mesa:', e);
        } finally {
            setVerificandoMesa(false);
        }
    }, []);

    // 1. Detección y validación de Mesa
    useEffect(() => {
        const token = searchParams.get('mesaToken') || routeParams.token || '';
        
        if (token) {
            verificarTokenMesa(token);
        } else {
            // Revisar si existe mesa previamente guardada en localStorage
            const guardada = localStorage.getItem('mesaActiva');
            if (guardada) {
                try {
                    const parsed = JSON.parse(guardada);
                    if (parsed && parsed.idMesa) {
                        setMesaActiva(parsed);
                        setNombreCliente(`Cliente ${parsed.mesa || 'Mesa'}`);
                        cargarPedidosMesa(parsed.idMesa);
                    }
                } catch (e) {
                    console.error(e);
                }
            }
            setVerificandoMesa(false);
        }

        // Cargar catálogo y configuración
        cargarProductos();
        cargarCategorias();

        fetchConfigFromDB().then(cfg => {
            if (cfg && cfg.usarPrecios !== undefined) {
                setUsarPrecios(cfg.usarPrecios !== 'no');
            }
        });

        const handleConfigChange = (e) => {
            if (e.detail && e.detail.usarPrecios !== undefined) {
                setUsarPrecios(e.detail.usarPrecios !== 'no');
            }
        };
        window.addEventListener('configUpdated', handleConfigChange);
        return () => window.removeEventListener('configUpdated', handleConfigChange);
    }, [searchParams, routeParams, verificarTokenMesa]);

    const cargarProductos = async () => {
        try {
            const res = await fetch(`${baseURL}/productosGet.php`);
            const data = await res.json();
            setProductos(data.productos || []);
        } catch (e) {
            console.error('Error al cargar productos:', e);
        }
    };

    const cargarCategorias = async () => {
        try {
            const res = await fetch(`${baseURL}/categoriasGet.php`);
            const data = await res.json();
            setCategorias(data.categorias || []);
        } catch (e) {
            console.error('Error al cargar categorías:', e);
        }
    };

    const cargarPedidosMesa = async (idMesa) => {
        if (!idMesa) return;
        try {
            const res = await fetch(`${baseURL}/pedidoGet.php`);
            const data = await res.json();
            const deEstaMesa = (data.pedidos || []).filter(p => p.idMesa === idMesa && p.estado !== 'Rechazado');
            setPedidosMesa(deEstaMesa.reverse());
        } catch (e) {
            console.error('Error al cargar pedidos de mesa:', e);
        }
    };

    // Auto refresco de pedidos de la mesa cada 10 segundos
    useEffect(() => {
        if (!mesaActiva?.idMesa) return;
        const interval = setInterval(() => {
            cargarPedidosMesa(mesaActiva.idMesa);
        }, 10000);
        return () => clearInterval(interval);
    }, [mesaActiva]);

    // Diccionario de categorías para mapeo rápido por idCategoria
    const mapaCategorias = useMemo(() => {
        const map = {};
        categorias.forEach(c => {
            map[String(c.idCategoria)] = c.categoria;
        });
        return map;
    }, [categorias]);

    // Categorías disponibles con productos publicados
    const categoriasDisponibles = useMemo(() => {
        return categorias.filter(c =>
            productos.some(p => String(p.idCategoria) === String(c.idCategoria))
        );
    }, [categorias, productos]);

    // Filtrar Productos Más Vendidos
    const masVendidos = useMemo(() => {
        return productos.filter(p => p.masVendido === 'si' || p.masVendido === '1' || p.masVendido === 'true').slice(0, 8);
    }, [productos]);

    // Filtrar Catálogo por categoría y búsqueda
    const productosFiltrados = useMemo(() => {
        return productos.filter(p => {
            const nombreCat = mapaCategorias[String(p.idCategoria)] || '';
            const busq = busqueda.toLowerCase().trim();
            const coincideTexto = !busq ||
                                 p.titulo?.toLowerCase().includes(busq) ||
                                 p.descripcion?.toLowerCase().includes(busq) ||
                                 nombreCat.toLowerCase().includes(busq);
            const coincideCat = categoriaSeleccionada === 'todas' || 
                                String(p.idCategoria) === String(categoriaSeleccionada);
            return coincideTexto && coincideCat;
        });
    }, [productos, busqueda, categoriaSeleccionada, mapaCategorias]);

    // Gestión del Carrito
    const agregarAlCarrito = (producto) => {
        const catNombre = mapaCategorias[String(producto.idCategoria)] || '';
        setCart(prev => {
            const exist = prev.find(item => item.idProducto === producto.idProducto);
            if (exist) {
                return prev.map(item =>
                    item.idProducto === producto.idProducto
                        ? { ...item, cantidad: item.cantidad + 1 }
                        : item
                );
            }
            return [...prev, {
                idProducto: producto.idProducto,
                titulo: producto.titulo,
                precio: Number(producto.precio) || 0,
                cantidad: 1,
                categoria: catNombre,
                imagen: producto.imagen1 || producto.imagen2 || ''
            }];
        });
    };

    const modificarCantidad = (idProducto, delta) => {
        setCart(prev => {
            return prev.map(item => {
                if (item.idProducto === idProducto) {
                    const n = item.cantidad + delta;
                    return n > 0 ? { ...item, cantidad: n } : null;
                }
                return item;
            }).filter(Boolean);
        });
    };

    const removerDelCarrito = (idProducto) => {
        setCart(prev => prev.filter(item => item.idProducto !== idProducto));
    };

    const totalCarrito = useMemo(() => {
        return cart.reduce((acc, item) => acc + (item.precio * item.cantidad), 0);
    }, [cart]);

    const totalItemsCarrito = useMemo(() => {
        return cart.reduce((acc, item) => acc + item.cantidad, 0);
    }, [cart]);

    // Enviar Pedido a Cocina (KDS)
    const handleEnviarPedidoCocina = async () => {
        if (!mesaActiva?.idMesa) {
            toast.error('No se ha detectado la mesa. Escanea nuevamente el código QR.');
            return;
        }

        if (cart.length === 0) {
            toast.warn('Tu carrito está vacío');
            return;
        }

        setEnviandoPedido(true);

        try {
            const payload = {
                idMesa: mesaActiva.idMesa,
                nombre: nombreCliente.trim() || `Cliente ${mesaActiva.mesa}`,
                nota: notaCocina.trim(),
                estado: 'Pendiente', // Va directo a KDS
                origen: 'qr',
                tipoEntrega: 'mesa',
                metodoPago: 'efectivo',
                estadoPago: 'pendiente',
                total: totalCarrito,
                productos: JSON.stringify(cart)
            };

            const res = await fetch(`${baseURL}/pedidoPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            setEnviandoPedido(false);

            if (data.idPedido || data.mensaje) {
                const numPedido = data.idPedido || '';
                Swal.fire({
                    title: '¡Pedido enviado a cocina!',
                    html: `Tu orden <b>#${numPedido}</b> ha sido recibida por el equipo de cocina.<br><br>🍳 Pronto comenzarán su preparación para la <b>${mesaActiva.mesa}</b>.`,
                    icon: 'success',
                    confirmButtonText: 'Ver estado de mi pedido',
                    confirmButtonColor: '#10b981'
                }).then(() => {
                    cargarPedidosMesa(mesaActiva.idMesa);
                    setModalPedidosActivos(true);
                });

                // Limpiar carrito
                setCart([]);
                setNotaCocina('');
                setDrawerCarritoAbierto(false);
                cargarPedidosMesa(mesaActiva.idMesa);
            } else {
                toast.error(data.error || 'Error al enviar el pedido');
            }
        } catch (e) {
            setEnviandoPedido(false);
            console.error(e);
            toast.error('Error de red al enviar el pedido');
        }
    };

    // Llamar al Mesero
    const handleLlamarMesero = async () => {
        if (!mesaActiva?.idMesa) {
            toast.error('No se detectó la mesa correspondiente');
            return;
        }

        setLlamandoMesero(true);
        try {
            const res = await fetch(`${baseURL}/llamadaMeseroPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idMesa: mesaActiva.idMesa,
                    motivo: motivoMesero
                })
            });
            const data = await res.json();
            setLlamandoMesero(false);
            setModalMeseroAbierto(false);

            if (data.idLlamada || data.mensaje) {
                Swal.fire({
                    title: '¡Mesero notificado!',
                    text: data.mensaje || `Un mesero se acercará a la ${mesaActiva.mesa} en breve.`,
                    icon: 'success',
                    timer: 3500,
                    showConfirmButton: false
                });
            } else {
                toast.error(data.error || 'No se pudo enviar la solicitud');
            }
        } catch (e) {
            setLlamandoMesero(false);
            console.error(e);
            toast.error('Error de red al llamar al mesero');
        }
    };

    return (
        <div className="menu-digital-page">
            <ToastContainer />

            {/* Header Superior Limpio */}
            <header className="menu-digital-header">
                <div className="menu-header-content">
                    <div className="menu-brand-info">
                        <img src={logo} alt="Logo" className="menu-logo-img" />
                        <span className="menu-table-badge">
                            🍽️ {mesaActiva ? mesaActiva.mesa : (verificandoMesa ? 'Detectando mesa...' : 'Mesa')}
                        </span>
                    </div>

                    <div className="menu-header-actions">
                        <button
                            type="button"
                            className="btn-header-call-waiter"
                            onClick={() => setModalMeseroAbierto(true)}
                        >
                            <FontAwesomeIcon icon={faBell} /> Llamar Mesero
                        </button>

                        {pedidosMesa.length > 0 && (
                            <button
                                type="button"
                                className="btn-header-my-orders"
                                onClick={() => setModalPedidosActivos(true)}
                                title="Ver pedidos de esta mesa"
                            >
                                <FontAwesomeIcon icon={faReceipt} /> ({pedidosMesa.length})
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Banner de Bienvenida a la Mesa */}
            <section className="menu-welcome-banner">
                <div className="menu-welcome-card">
                    <div className="menu-welcome-text">
                        <h2>
                            ¡Te damos la bienvenida a <span className="welcome-mesa-highlight">{mesaActiva ? mesaActiva.mesa : 'tu mesa'}</span>!
                        </h2>
                        <p>Explora nuestra carta gastronómica y realiza tus pedidos directo a cocina.</p>
                    </div>
                    <div className="menu-welcome-icon-wrap">
                        <FontAwesomeIcon icon={faUtensils} className="menu-welcome-icon" />
                    </div>
                </div>
            </section>

            {/* Buscador de Platos */}
            <div className="menu-search-wrapper">
                <div className="menu-search-box">
                    <FontAwesomeIcon icon={faSearch} className="menu-search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar platos, bebidas o postres..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="menu-search-input"
                    />
                    {busqueda && (
                        <button className="menu-search-clear" onClick={() => setBusqueda('')}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>
            </div>

            {/* Barra Horizontal de Categorías Deslizables */}
            <div className="menu-categories-scroll-wrapper">
                <div className="menu-categories-scroll">
                    <button
                        type="button"
                        className={`menu-cat-btn ${categoriaSeleccionada === 'todas' ? 'active' : ''}`}
                        onClick={() => setCategoriaSeleccionada('todas')}
                    >
                        Todas
                    </button>
                    {categoriasDisponibles.map(cat => (
                        <button
                            key={cat.idCategoria}
                            type="button"
                            className={`menu-cat-btn ${String(categoriaSeleccionada) === String(cat.idCategoria) ? 'active' : ''}`}
                            onClick={() => setCategoriaSeleccionada(cat.idCategoria)}
                        >
                            {cat.categoria}
                        </button>
                    ))}
                </div>
            </div>

            {/* Sección: Platos Más Vendidos (Destacados) */}
            {categoriaSeleccionada === 'todas' && !busqueda && masVendidos.length > 0 && (
                <section className="menu-bestsellers-section">
                    <div className="section-title-wrap">
                        <h3><FontAwesomeIcon icon={faFire} style={{ color: '#ef4444' }} /> Platos Más Populares</h3>
                    </div>
                    <div className="bestsellers-scroll-container">
                        {masVendidos.map(prod => {
                            const enCart = cart.find(i => i.idProducto === prod.idProducto);
                            return (
                                <div key={prod.idProducto} className="bestseller-card">
                                    <span className="bestseller-badge">Popular</span>
                                    <div className="bestseller-img-wrap">
                                        {prod.imagen1 ? (
                                            <img src={prod.imagen1} alt={prod.titulo} />
                                        ) : (
                                            <FontAwesomeIcon icon={faUtensils} className="dish-placeholder" />
                                        )}
                                    </div>
                                    <div className="bestseller-info">
                                        <h4>{prod.titulo}</h4>
                                        <div className="bestseller-bottom">
                                            {usarPrecios && (
                                                <span className="bestseller-price">{moneda} {prod.precio}</span>
                                            )}
                                            {enCart ? (
                                                <div className="dish-qty-stepper">
                                                    <button onClick={() => modificarCantidad(prod.idProducto, -1)}>-</button>
                                                    <span className="dish-qty-val">{enCart.cantidad}</span>
                                                    <button onClick={() => modificarCantidad(prod.idProducto, 1)}>+</button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn-dish-add"
                                                    onClick={() => agregarAlCarrito(prod)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} /> Pedir
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Catálogo Principal */}
            <main className="menu-catalog-section">
                <div className="section-title-wrap">
                    <h3>
                        {categoriaSeleccionada === 'todas'
                            ? (busqueda ? `Resultados para "${busqueda}"` : 'Toda la Carta')
                            : (mapaCategorias[String(categoriaSeleccionada)] || 'Categoría')
                        }
                    </h3>
                    <span>{productosFiltrados.length} productos</span>
                </div>

                {productosFiltrados.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                        <FontAwesomeIcon icon={faUtensils} style={{ fontSize: '36px', marginBottom: '10px' }} />
                        <p>No se encontraron productos disponibles en esta categoría.</p>
                    </div>
                ) : (
                    <div className="menu-products-grid">
                        {productosFiltrados.map(prod => {
                            const enCart = cart.find(i => i.idProducto === prod.idProducto);

                            return (
                                <div key={prod.idProducto} className="menu-dish-card">
                                    <div className="dish-img-wrap">
                                        {prod.imagen1 ? (
                                            <img src={prod.imagen1} alt={prod.titulo} />
                                        ) : (
                                            <FontAwesomeIcon icon={faUtensils} className="dish-placeholder" />
                                        )}
                                    </div>

                                    <div className="dish-info-wrap">
                                        <div>
                                            <span className="dish-category-tag">
                                                {mapaCategorias[String(prod.idCategoria)] || ''}
                                            </span>
                                            <h4 className="dish-title">{prod.titulo}</h4>
                                            {prod.descripcion && (
                                                <p className="dish-desc">{prod.descripcion}</p>
                                            )}
                                        </div>

                                        <div className="dish-bottom-row">
                                            {usarPrecios ? (
                                                <span className="dish-price">{moneda} {prod.precio}</span>
                                            ) : (
                                                <span></span>
                                            )}

                                            {enCart ? (
                                                <div className="dish-qty-stepper">
                                                    <button onClick={() => modificarCantidad(prod.idProducto, -1)}>-</button>
                                                    <span className="dish-qty-val">{enCart.cantidad}</span>
                                                    <button onClick={() => modificarCantidad(prod.idProducto, 1)}>+</button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn-dish-add"
                                                    onClick={() => agregarAlCarrito(prod)}
                                                >
                                                    <FontAwesomeIcon icon={faPlus} /> Agregar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>

            {/* Barra Flotante de Carrito en la parte inferior */}
            {totalItemsCarrito > 0 && (
                <div
                    className="menu-floating-cart-bar"
                    onClick={() => setDrawerCarritoAbierto(true)}
                >
                    <div className="floating-cart-left">
                        <span className="floating-cart-badge">{totalItemsCarrito}</span>
                        <span className="floating-cart-title">Ver mi comanda</span>
                    </div>

                    <div className="floating-cart-right">
                        {usarPrecios && (
                            <span className="floating-cart-total">{moneda} {totalCarrito.toFixed(2)}</span>
                        )}
                        <FontAwesomeIcon icon={faArrowRight} className="floating-cart-arrow" />
                    </div>
                </div>
            )}

            {/* Modal / Drawer del Carrito */}
            {drawerCarritoAbierto && (
                <div className="menu-cart-modal-overlay" onClick={() => setDrawerCarritoAbierto(false)}>
                    <div className="menu-cart-drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="drawer-header">
                            <div>
                                <h3>🛒 Comanda de {mesaActiva ? mesaActiva.mesa : 'Mesa'}</h3>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                    {totalItemsCarrito} {totalItemsCarrito === 1 ? 'artículo seleccionado' : 'artículos seleccionados'}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="drawer-close-btn"
                                onClick={() => setDrawerCarritoAbierto(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="drawer-body">
                            <div className="drawer-cart-items">
                                {cart.map(item => (
                                    <div key={item.idProducto} className="drawer-item-row">
                                        <div className="drawer-item-left">
                                            <span className="drawer-item-title">{item.titulo}</span>
                                            {usarPrecios && (
                                                <span className="drawer-item-unit-price">{moneda} {item.precio} c/u</span>
                                            )}
                                        </div>

                                        <div className="dish-qty-stepper">
                                            <button onClick={() => modificarCantidad(item.idProducto, -1)}>-</button>
                                            <span className="dish-qty-val">{item.cantidad}</span>
                                            <button onClick={() => modificarCantidad(item.idProducto, 1)}>+</button>
                                        </div>

                                        {usarPrecios && (
                                            <div className="drawer-item-subtotal">
                                                {moneda} {(item.precio * item.cantidad).toFixed(2)}
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            onClick={() => removerDelCarrito(item.idProducto)}
                                            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px' }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="drawer-client-inputs">
                                <div className="drawer-input-group">
                                    <label>Nombre de quien pide (opcional):</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: Carlos"
                                        value={nombreCliente}
                                        onChange={(e) => setNombreCliente(e.target.value)}
                                        className="drawer-input"
                                    />
                                </div>

                                <div className="drawer-input-group">
                                    <label>Instrucciones u observaciones para cocina:</label>
                                    <input
                                        type="text"
                                        placeholder="Ej: Término medio, sin cebolla, aderezo aparte..."
                                        value={notaCocina}
                                        onChange={(e) => setNotaCocina(e.target.value)}
                                        className="drawer-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="drawer-footer">
                            {usarPrecios && (
                                <div className="drawer-total-row">
                                    <span>Total Estimado:</span>
                                    <strong>{moneda} {totalCarrito.toFixed(2)}</strong>
                                </div>
                            )}

                            <button
                                type="button"
                                className="btn-send-to-kitchen"
                                onClick={handleEnviarPedidoCocina}
                                disabled={enviandoPedido || cart.length === 0}
                            >
                                <FontAwesomeIcon icon={faUtensils} />
                                {enviandoPedido ? 'Enviando a cocina...' : '🍳 Confirmar y Enviar a Cocina'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Llamar Mesero */}
            {modalMeseroAbierto && (
                <div className="menu-cart-modal-overlay" onClick={() => setModalMeseroAbierto(false)}>
                    <div className="menu-cart-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
                        <div className="drawer-header">
                            <h3>🛎️ Llamar al Mesero</h3>
                            <button
                                type="button"
                                className="drawer-close-btn"
                                onClick={() => setModalMeseroAbierto(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="drawer-body">
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                                Selecciona el motivo para que el mesero asignado a <strong>{mesaActiva?.mesa}</strong> se acerque con lo que necesitas:
                            </p>

                            <div className="call-waiter-options">
                                {[
                                    '🙋 Atención en mesa',
                                    '🧾 Solicitar la cuenta',
                                    '🥢 Cubiertos / Servilletas',
                                    '🧊 Hielo / Vaso adicional',
                                    '❓ Consulta sobre la carta'
                                ].map(opc => (
                                    <button
                                        key={opc}
                                        type="button"
                                        className={`call-option-btn ${motivoMesero === opc ? 'selected' : ''}`}
                                        onClick={() => setMotivoMesero(opc)}
                                    >
                                        <span>{opc}</span>
                                    </button>
                                ))}
                            </div>

                            <button
                                type="button"
                                className="btn-confirm-call-waiter"
                                onClick={handleLlamarMesero}
                                disabled={llamandoMesero}
                            >
                                <FontAwesomeIcon icon={faBell} />
                                {llamandoMesero ? 'Notificando mesero...' : 'Enviar Solicitud'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Estado de Mis Pedidos (KDS Tracker en vivo) */}
            {modalPedidosActivos && (
                <div className="menu-cart-modal-overlay" onClick={() => setModalPedidosActivos(false)}>
                    <div className="menu-cart-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
                        <div className="drawer-header">
                            <h3>📋 Pedidos de {mesaActiva?.mesa}</h3>
                            <button
                                type="button"
                                className="drawer-close-btn"
                                onClick={() => setModalPedidosActivos(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="drawer-body">
                            {pedidosMesa.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#94a3b8', margin: '20px 0' }}>
                                    No hay pedidos registrados en esta mesa todavía.
                                </p>
                            ) : (
                                <div className="active-orders-list">
                                    {pedidosMesa.map(p => {
                                        let badgeClase = 'pendiente';
                                        let icono = '🕒';
                                        if (p.estado === 'En preparación') {
                                            badgeClase = 'preparacion';
                                            icono = '🍳';
                                        } else if (p.estado === 'Listo') {
                                            badgeClase = 'listo';
                                            icono = '✅';
                                        } else if (p.estado === 'Entregado' || p.estado === 'Pagado') {
                                            badgeClase = 'entregado';
                                            icono = '🍽️';
                                        }

                                        let itemsArr = [];
                                        try {
                                            itemsArr = typeof p.productos === 'string' ? JSON.parse(p.productos) : p.productos;
                                        } catch (e) {
                                            itemsArr = [];
                                        }

                                        return (
                                            <div key={p.idPedido} className="active-order-item-card">
                                                <div className="active-order-header">
                                                    <strong>Orden #{p.idPedido}</strong>
                                                    <span className={`badge-order-status ${badgeClase}`}>
                                                        {icono} {p.estado}
                                                    </span>
                                                </div>

                                                <div className="active-order-prods-list">
                                                    {itemsArr.map((it, idx) => (
                                                        <div key={idx}>• {it.cantidad}x {it.titulo}</div>
                                                    ))}
                                                </div>

                                                {usarPrecios && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '700' }}>
                                                        <span>Total:</span>
                                                        <span>{moneda} {parseFloat(p.total).toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
